import {
  getProjectDataKeysToWaitFor,
  ProjectDataKey,
} from 'shared/domain/projectData/projectDataModel';
import { LogLevel } from 'shared/types/logger';

/** Why the selected project's data could not be loaded. */
export enum ProjectDataLoadingFailureReason {
  /** The worker's download of some data ended without it (e.g. offline, a server error). */
  downloadFailed = 'download-failed',
  /** The worker cannot start the download again: nothing progresses. */
  stalled = 'stalled',
  /** The worker does not answer. */
  workerUnresponsive = 'worker-unresponsive',
  /** The API refused the project's data: the user has no access to the project (any more). */
  noProjectAccess = 'no-project-access',
}

/** A state of the account, not a failure of the app: trying again changes nothing. */
export function isNoProjectAccessFailureReason(reason: ProjectDataLoadingFailureReason): boolean {
  return reason === ProjectDataLoadingFailureReason.noProjectAccess;
}

/** A lost access is logged as info: it is expected, not an error of the app. */
export function getProjectDataLoadingFailureLogLevel(reason: ProjectDataLoadingFailureReason): LogLevel {
  return isNoProjectAccessFailureReason(reason) ? LogLevel.INFO : LogLevel.ERROR;
}

export type ProjectDataLoadingFailure = Readonly<{
  reason: ProjectDataLoadingFailureReason;
  keys: readonly ProjectDataKey[];
  /** The worker is older than the page: refreshing helps, trying again does not. */
  needsWorkerUpdate: boolean;
}>;

export type ProjectDataLoading = Readonly<{
  /** Data still being downloaded by the worker. */
  pending: Readonly<Record<ProjectDataKey, boolean>>;
  waitsForInspectionData: boolean;
  /** Set when the loading ended without the data; the project shows an error instead. */
  failure: ProjectDataLoadingFailure | null;
}>;

function createAllPending(): Record<ProjectDataKey, boolean> {
  return {
    [ProjectDataKey.issues]: true,
    [ProjectDataKey.templates]: true,
    [ProjectDataKey.inspections]: true,
    [ProjectDataKey.companies]: true,
    [ProjectDataKey.contracts]: true,
    [ProjectDataKey.users]: true,
    [ProjectDataKey.sites]: true,
    [ProjectDataKey.levels]: true,
    [ProjectDataKey.documentations]: true,
    [ProjectDataKey.directories]: true,
    [ProjectDataKey.issueForms]: true,
    [ProjectDataKey.visibleFields]: true,
  };
}

export function createInitialProjectDataLoading(): ProjectDataLoading {
  return { pending: createAllPending(), waitsForInspectionData: true, failure: null };
}

export function setProjectDataPending(
  loading: ProjectDataLoading,
  key: ProjectDataKey,
  pending: boolean
): ProjectDataLoading {
  return loading.pending[key] === pending
    ? loading
    : { ...loading, pending: { ...loading.pending, [key]: pending } };
}

export function markAllProjectDataPending(loading: ProjectDataLoading): ProjectDataLoading {
  return { ...createInitialProjectDataLoading(), waitsForInspectionData: loading.waitsForInspectionData };
}

/**
 * The first failure is the one shown and logged; later ones of the same loading add nothing,
 * except a lost access to the project: it explains the earlier ones (another kind of data may
 * have failed first, without the API's answer) and is the only one the user can act on.
 */
export function replacesProjectDataLoadingFailure(
  loading: ProjectDataLoading,
  reason: ProjectDataLoadingFailureReason
): boolean {
  if (!loading.failure) {
    return true;
  }
  return isNoProjectAccessFailureReason(reason) && !isNoProjectAccessFailureReason(loading.failure.reason);
}

export function setProjectDataLoadingFailure(
  loading: ProjectDataLoading,
  failure: ProjectDataLoadingFailure
): ProjectDataLoading {
  return replacesProjectDataLoadingFailure(loading, failure.reason) ? { ...loading, failure } : loading;
}

export function markProjectDataLoadingNeedsWorkerUpdate(loading: ProjectDataLoading): ProjectDataLoading {
  return loading.failure && !loading.failure.needsWorkerUpdate
    ? { ...loading, failure: { ...loading.failure, needsWorkerUpdate: true } }
    : loading;
}

export function clearProjectDataLoadingFailure(loading: ProjectDataLoading): ProjectDataLoading {
  return loading.failure ? { ...loading, failure: null } : loading;
}

export function getPendingProjectData(loading: ProjectDataLoading): ProjectDataKey[] {
  return getProjectDataKeysToWaitFor(loading.waitsForInspectionData).filter((key) => loading.pending[key]);
}

export function isProjectDataLoading(loading: ProjectDataLoading): boolean {
  return getPendingProjectData(loading).length > 0;
}
